import React, { useState, useContext } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { API_DOMAIN } from '../../Reusable_Components/Config/apiConfig';
import katibuLogo from '../../../assets/katibu-logo.png';
import * as listCache from '../../../services/listCache';
import { usePwaInstall } from '../../../hooks/usePwaInstall';
import { consumeLogoutReason, consumeReturnLocation, getLogoutToast } from '../../../services/sessionManager';
import { ToastContext } from '../../../context/ToastContext';

/* ── Download icon (install link prefix) ──────────────────────── */
const DownloadIcon = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
    <path d="M8 2v8m0 0l-3-3m3 3l3-3" stroke="currentColor" strokeWidth="1.4"
      strokeLinecap="round" strokeLinejoin="round" />
    <path d="M2.5 12v1.5A1.5 1.5 0 0 0 4 15h8a1.5 1.5 0 0 0 1.5-1.5V12"
      stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

/* ── User icon (input prefix) ─────────────────────────────────── */
const UserIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="5.5" r="3" stroke="#3a4055" strokeWidth="1.4" />
    <path d="M2 14 C2 11 4.7 9 8 9 C11.3 9 14 11 14 14"
      stroke="#3a4055" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

/* ── Lock icon (input prefix) ─────────────────────────────────── */
const LockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <rect x="3" y="7" width="10" height="7" rx="2"
      stroke="#3a4055" strokeWidth="1.4" />
    <path d="M5 7 V5 C5 3.3 6.3 2 8 2 C9.7 2 11 3.3 11 5 V7"
      stroke="#3a4055" strokeWidth="1.4" strokeLinecap="round" />
    <circle cx="8" cy="10.5" r="1" fill="#3a4055" />
  </svg>
);

/* ── Main Component ──────────────────────────────────────────── */
const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { canInstall, install } = usePwaInstall();
  const navigate = useNavigate();
  const toast = useContext(ToastContext);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    try {
      setLoading(true);
      const response = await axios.post(`${API_DOMAIN}security/audit/password-login/`, {
        username,
        password,
      });
      listCache.invalidateAll(); // don't carry over another user's cached list data in this tab
      localStorage.setItem('customer_management_token', response.data.token);
      localStorage.setItem('token', response.data.token);
      if (response.data.username) localStorage.setItem('username', response.data.username);
      if (response.data.is_superadmin !== undefined) {
        localStorage.setItem('is_superadmin', String(response.data.is_superadmin));
      }
      // Store tenant credentials for the axios interceptor.
      // Absent for superadmins — the backend bypasses tenant checks for them.
      if (response.data.tenant_id) {
        localStorage.setItem('crm_tenant_id', response.data.tenant_id);
      } else {
        localStorage.removeItem('crm_tenant_id');
      }
      if (response.data.tenant_token) {
        localStorage.setItem('crm_tenant_token', response.data.tenant_token);
      } else {
        localStorage.removeItem('crm_tenant_token');
      }

      // Show session-expiry toast if applicable, then navigate back
      // (consumed one-shot so the message never repeats on subsequent logins)
      const reason = consumeLogoutReason();
      const returnTo = consumeReturnLocation();
      const toastCfg = getLogoutToast(reason);
      if (toastCfg) toast?.addToast(toastCfg);
      navigate(
        returnTo ? { pathname: returnTo.pathname, search: returnTo.search, hash: returnTo.hash } : '/dashboard',
        { replace: true }
      );
    } catch (err) {
      setError('Invalid username or password. Please check your credentials and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className={`login-top-loader${loading ? ' login-top-loader--active' : ''}`} />

      {/* Logo */}
      <div className="login-logo">
        <img src={katibuLogo} alt="Katibu — Limitless Growth" className="login-logo-img" />
      </div>

      {/* Card */}
      <div className="login-card">
        <div className="login-card-system-name">
          <span className="login-card-system-name-crm">Customer Relationship</span>
          <span className="login-card-system-name-mgmt">Management</span>
        </div>
        <div className="login-card-divider" />
        <h1 className="login-card-title">System Authentication</h1>
        <p className="login-card-subtitle">
          Enter your username and password to continue.
        </p>

        {error && <div className="login-error">{error}</div>}

        <form className="login-fields" onSubmit={handleLogin}>

          {/* Username */}
          <div className="login-field-group">
            <label className="login-field-label">Username</label>
            <div className="login-input-wrap">
              <span className="login-input-icon"><UserIcon /></span>
              <input
                type="text"
                className="login-input"
                placeholder="Enter staff username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoComplete="username"
                autoFocus
              />
            </div>
          </div>

          {/* Password */}
          <div className="login-field-group">
            <label className="login-field-label">Passcode / Password</label>
            <div className="login-input-wrap">
              <span className="login-input-icon"><LockIcon /></span>
              <input
                type="password"
                className="login-input"
                placeholder="Enter access passcode"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </div>
          </div>

          {/* Forgot password */}
          <div className="fp-back-row">
            <Link to="/forgot-password" className="fp-back-link">Forgot password?</Link>
          </div>

          {/* Submit */}
          <button type="submit" className="login-btn" disabled={loading}>
            {loading ? (
              <>
                <div className="login-spinner" />
                Authenticating…
              </>
            ) : (
              <>
                <span className="login-btn-arrow">→</span>
                Authenticate &amp; Clock In
              </>
            )}
          </button>

        </form>

        {/* Install app */}
        {canInstall && (
          <button type="button" className="login-install-link" onClick={install}>
            <DownloadIcon />
            Download the app
          </button>
        )}

        {/* Footer */}
        <div className="login-card-footer">
          <img src={katibuLogo} alt="Katibu" className="login-footer-logo" />
          <span className="login-footer-text">
            ERP v3.1.2 &nbsp;•&nbsp; Secure Session Lock &nbsp;•&nbsp; Licensed to Main Branch
          </span>
        </div>
      </div>

    </div>
  );
};

export default LoginPage;
